import type { CSSProperties } from 'react'
import type { Transition } from 'framer-motion'

import {
  mergeReducedMotionPreset,
  resolveExpandTransition,
  resolveTransition,
  type ThemeMotionPreset,
} from '../../animation/themeMotion'

/** 灵动岛统一加速系数：保持主题曲线，整体提速 */
const ISLAND_MOTION_SPEED = 0.72

function transitionEaseCss(transition: Transition): string {
  const ease = transition.ease
  if (ease === 'linear') return 'linear'
  if (typeof ease === 'string') return ease
  if (Array.isArray(ease) && ease.length >= 4) {
    return `cubic-bezier(${ease.slice(0, 4).join(', ')})`
  }
  return 'cubic-bezier(0.22, 1, 0.36, 1)'
}

function transitionDurationSec(transition: Transition, fallback = 0.2): number {
  const dur = transition.duration
  if (typeof dur === 'number' && Number.isFinite(dur)) return dur
  if (typeof dur === 'string' && /^[\d.]+/.test(dur)) {
    return parseFloat(dur) / (/ms$/iu.test(dur) ? 1000 : 1)
  }
  return fallback
}

function scaleDurationValue(value: number | string | undefined, speed: number): number | undefined {
  if (value == null) return value
  if (typeof value === 'number' && Number.isFinite(value)) return value * speed
  if (typeof value === 'string' && /^[\d.]+/.test(value)) {
    const parsed = parseFloat(value)
    if (!Number.isFinite(parsed)) return undefined
    return /ms$/iu.test(value) ? (parsed * speed) / 1000 : parsed * speed
  }
  return undefined
}

function speedUpTransition(transition: Transition, speed = ISLAND_MOTION_SPEED): Transition {
  const next: Transition = { ...transition }
  next.duration = scaleDurationValue(transition.duration, speed)
  next.delay = scaleDurationValue(transition.delay, speed)

  if (transition.type === 'spring') {
    const stiffness =
      typeof transition.stiffness === 'number' ? transition.stiffness : 320
    const damping = typeof transition.damping === 'number' ? transition.damping : 28
    const mass = typeof transition.mass === 'number' ? transition.mass : 0.75
    next.stiffness = Math.round(stiffness * 1.12)
    next.damping = Math.round(damping * 1.06)
    next.mass = Math.max(0.55, mass * 0.92)
  }

  return next
}

export function resolveDynamicIslandMotion(preset: ThemeMotionPreset, reducedMotion: boolean) {
  const motion = mergeReducedMotionPreset(preset, reducedMotion)
  const expand = speedUpTransition(resolveExpandTransition(motion, { reducedMotion }))
  const reveal = speedUpTransition(resolveTransition(motion))
  const duration = transitionDurationSec(expand)
  const ease = transitionEaseCss(expand)
  const revealDuration = transitionDurationSec(reveal, motion.duration)

  return {
    expand,
    reveal,
    duration,
    ease,
    revealDuration,
    enterY: reducedMotion ? 0 : Math.min(motion.enterY * 0.74, 8),
    enterScale: reducedMotion ? 1 : motion.enterScale,
    cssVars: {
      '--island-motion-duration': `${duration}s`,
      '--island-motion-ease': ease,
      '--island-motion-reveal-duration': `${revealDuration}s`,
    } as CSSProperties,
  }
}
